import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, Loader, Lu, fmtSAR } from "@/components/shared";
import { Button } from "@/components/ui/button";

export default function Reports() {
  const { t, lang } = useLang();
  const { selectedId, selected } = useProperty();
  const [energy, setEnergy] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!selectedId) return;
    setEnergy(null);
    Promise.all([
      client.get(`/properties/${selectedId}/energy`),
      client.get(`/properties/${selectedId}/rooms`),
      client.get(`/properties/${selectedId}/overview`),
    ]).then(([e, r, o]) => { setEnergy(e.data.series || []); setRooms(r.data || []); setOverview(o.data); });
  }, [selectedId]);

  if (!energy || !overview) return <Loader />;
  const pname = selected?.[lang === "ar" ? "name_ar" : "name_en"] || "";

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const summary = [
      ["Madarek Report", pname],
      ["Total Rooms", overview.total_rooms],
      ["Occupancy %", overview.occupancy_pct],
      ["Open Alerts", overview.open_alerts],
      ["Open Tickets", overview.open_tickets],
      ["Energy Saved (kWh/month)", overview.energy_month_saved_kwh],
      ["Energy Saved (SAR/month)", overview.energy_month_saved_sar],
      ["Avg Temp", overview.avg_temp],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(energy), "Energy");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rooms.map((r) => ({
      Room: r.number, Floor: r.floor, Type: r.type, Status: r.status, Temp: r.temp, AC: r.ac_on, EnergyToday: r.energy_today,
    }))), "Rooms");
    XLSX.writeFile(wb, `Madarek_Report_${selectedId}.xlsx`);
    toast.success(t("exportExcel"));
  };

  return (
    <div>
      <SectionHeader icon="file-text" title={t("reports")} subtitle={t("reportsIntro")}>
        <div className="flex gap-2">
          <Button data-testid="report-pdf" onClick={() => window.print()} className="bg-foreground/5 hover:bg-foreground/10 border border-border text-foreground/80">
            <Lu name="printer" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{t("exportPdf")}
          </Button>
          <Button data-testid="report-excel" onClick={exportExcel} className="md-gold-gradient text-primary-foreground font-bold border-0">
            <Lu name="sheet" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{t("exportExcel")}
          </Button>
        </div>
      </SectionHeader>

      <div className="md-card p-6 print:bg-white">
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-border">
          <div className="w-11 h-11 rounded-lg md-gold-gradient flex items-center justify-center"><Lu name="hotel" className="w-6 h-6 text-primary-foreground" /></div>
          <div>
            <h2 className="text-xl font-bold text-foreground">{pname}</h2>
            <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["totalRooms", overview.total_rooms, ""],
            ["occupancyRate", `${overview.occupancy_pct}%`, ""],
            ["energySaved", fmtSAR(overview.energy_month_saved_sar), t("sar")],
            ["avgTemp", `${overview.avg_temp}°`, ""],
            ["openAlerts", overview.open_alerts, ""],
            ["openTickets", overview.open_tickets, ""],
            ["devicesOnline", `${overview.devices_online_pct}%`, ""],
            ["energySaved", `${fmtSAR(overview.energy_month_saved_kwh)}`, "kWh"],
          ].map(([k, v, u], i) => (
            <div key={i} className="p-4 rounded-lg bg-foreground/5">
              <p className="text-xs text-muted-foreground/70">{t(k)}</p>
              <p className="text-2xl font-extrabold font-mono text-primary mt-1">{v} <span className="text-sm text-muted-foreground/70">{u}</span></p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
